import { CTA, DesignTokenSizeEnum, Divider, Fieldset, Layout, Text } from "@volkswagen-onehub/components-core";
import { Alert, Magnifier } from '@volkswagen-onehub/icons-core';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { Dealer } from '../../modules/entities/Entities';
import { RadioButtonField } from '../../modules/forms/fields/RadioButtonField';
import { SelectDealerPageViewProps } from './SelectDealerPageViewProps';
import { Form } from '../../modules/forms/Form';
import { SelectDealerPageFormSchema } from './SelectDealerPageFormSchema';
import { TextInputField } from '../../modules/forms/fields/TextInputField';
import { useTracking } from '../../tracking';

const StyledText = styled(Text)`
  margin-bottom: ${(props) => props.theme.size.static250};
`;
const StyledCTAMore = styled(CTA)`
  text-decoration: underline;
`;
const StyledCTAMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.size.static350};
`;
const StyledNotification = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${(props) => props.theme.size.static250};
  margin-top: ${(props) => props.theme.size.static400};

  & span {
    display: block;
  }
`;
const StyledDividerWrapper = styled.div`
  margin-bottom: ${(props) => props.theme.size.static350};
`;

interface DealerFieldProps {
  dealer: Dealer;
  dealers: Dealer[];
  onDealerChange?: (dealerCode: string) => void;
}

function DealerField({ dealer, dealers, onDealerChange }: DealerFieldProps): ReactElement {
  const trackingService = useTracking();

  return (
    <Layout rowGap={DesignTokenSizeEnum.static150} allowOverflowingContent>
      <RadioButtonField
        name="selectedDealerCode"
        value={dealer.dealerCode}
        label={dealer.name}
        onChange={() => {
          trackingService.trackDealerSelectClick({
            contentId: 'dealer',
            partner: {
                informationCountry: 'ZA',
                informationBrand: dealer.brand,
                informationName: dealer.name,
                informationZIP: dealer.postalCode,
                informationBnr: dealer.dealerCode,
                SearchNumberOfResults: dealers.length
            }
          });
          onDealerChange?.(dealer.dealerCode);
        }}
      />
      <div>
        <Text>
          {dealer.address} & {dealer.suburb}
        </Text>
        <Text>{dealer.city}</Text>
        <Text>{dealer.country}</Text>
      </div>
    </Layout>
  );
}

export function SelectDealerPageVwView({
  initialFormData,
  onSearchKeywordChange,
  onFormChanged,
  formRef,
  noDealersFound,
  searchingBykeyword,
  preferredDealer,
  loadMoreDealersEnabled,
  onLoadMoreDealers,
  dealers,
  onDealerChange,
}: SelectDealerPageViewProps): ReactElement {

  const onClear = (): void => {
    onFormChanged();
    onSearchKeywordChange('');
  };
  const onLoadMoreDealersWrapper = (e: React.MouseEvent) => {
    e.preventDefault();
    onLoadMoreDealers();
  };

  return (
    <Layout allowOverflowingContent>
      <Form schema={SelectDealerPageFormSchema} defaultValues={initialFormData} onChange={onFormChanged} ref={formRef}>
        <TextInputField
          type="text"
          name="dealerSearch"
          label="Select your preferred dealer below"
          onChange={(v) => {
            onSearchKeywordChange(v);
          }}
          icon={<Magnifier />}
          onClear={onClear}
          isFloating
          hasClearButton
        />
        <StyledText>You could search by dealer name, city/town or postal code</StyledText>
        <Fieldset>
          {!!preferredDealer && <DealerField dealer={preferredDealer} dealers={dealers} onDealerChange={onDealerChange}/>}
          {searchingBykeyword &&
            dealers.map((dealer) => (
              <div key={dealer.dealerCode}>
                <StyledDividerWrapper>
                  <Divider />
                </StyledDividerWrapper>
                <DealerField dealer={dealer} dealers={dealers} onDealerChange={onDealerChange}/>
              </div>
            ))}
        </Fieldset>
        {loadMoreDealersEnabled && (
          <StyledCTAMoreWrapper>
            <StyledCTAMore tag="button" emphasis="tertiary" onClick={onLoadMoreDealersWrapper}>
              Load more
            </StyledCTAMore>
          </StyledCTAMoreWrapper>
        )}
        {noDealersFound && (
          <StyledNotification>
            <Alert />
            <Text>
              No dealers found <span>Try another search keyword</span>
            </Text>
          </StyledNotification>
        )}
      </Form>
    </Layout>
  );
}
